import { exists } from 'base/helpers';
import { Badge } from 'base/ui/badge';
import { Button } from 'base/ui/button';
import { InvisibleForm, RedErrorText, useSubmit } from 'base/ui/form';
import { IconLink, IconOption } from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Inline, Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { MenuButton } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { DividerLine } from 'base/ui/panel';
import { TextRegularLight, TextSmallLight } from 'base/ui/typography';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from './invite_team_form.css';
import { manageAccountRouteToUrl } from './routes';

export function InviteTeamForm({
  onDone,
  invite,
  inviteLinkInfo,
  onCopyLink,
  onResetLink,
  onToggleLinks,
}: {
  onDone(): void;
  invite: (emails: readonly string[]) => Promise<void>;
  inviteLinkInfo: {
    enabled: boolean;
    invitationLink: string | null;
  };
  onCopyLink: () => Promise<void>;
  onResetLink: () => Promise<void>;
  onToggleLinks: (enabled: boolean) => Promise<void>;
}) {
  const blurTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  const [input, setInput] = React.useState<{
    chipEmails: readonly string[];
    composing: string;
    composingError: string;
  }>({ chipEmails: [], composing: '', composingError: '' });
  const emails = [
    ...input.chipEmails,
    EMAIL_REGEX.test(input.composing) ? input.composing : undefined,
  ].filter(exists);

  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [loading, formError, handleSubmit] = useSubmit(async () => {
    if (emails.length === 0) return;
    await invite(emails);
    onDone();
  });

  const manageUrl = manageAccountRouteToUrl({
    path: 'manage-account/plan',
  });

  function maybeAddComposing() {
    setInput((input) => {
      if (input.composing.trim().length === 0) {
        return { ...input, composingError: '' };
      }
      if (EMAIL_REGEX.test(input.composing)) {
        return {
          ...input,
          composingError: '',
          chipEmails: setAppend(input.chipEmails, input.composing),
          composing: '',
        };
      } else {
        return {
          ...input,
          composingError: 'Please enter a valid email address',
        };
      }
    });
  }

  return (
    <InvisibleForm disabled={loading} onSubmit={handleSubmit} loading={loading}>
      <div className={styles.width}>
        <Rows spacing={24}>
          <Rows spacing={8}>
            <TextSmallLight>Invite link</TextSmallLight>
            <Inline spacing={8} yalign="center" xalign="space-between">
              <div className={styles.linkContent}>
                <div className={styles.linkContainer}>
                  <TextRegularLight
                    lineClamp={1}
                    color={
                      inviteLinkInfo.enabled && inviteLinkInfo.invitationLink
                        ? 'inherit'
                        : 'grey'
                    }
                  >
                    {inviteLinkInfo.enabled && inviteLinkInfo.invitationLink
                      ? inviteLinkInfo.invitationLink
                      : 'Invite links are disabled'}
                  </TextRegularLight>
                </div>
                <div className={styles.fixedWidthButtonContainer}>
                  <Button
                    variant="primary"
                    size="large"
                    icon={IconLink}
                    label="Copy Link"
                    onClick={onCopyLink}
                    fullWidth={true}
                    disabled={
                      !inviteLinkInfo.enabled || !inviteLinkInfo.invitationLink
                    }
                  />
                </div>
                <InviteLinkOptionsMenu
                  enabled={inviteLinkInfo.enabled}
                  onReset={onResetLink}
                  onToggle={onToggleLinks}
                />
              </div>
            </Inline>
          </Rows>

          <Inline yalign="center">
            <DividerLine />
            <TextRegularLight color="grey">or</TextRegularLight>
            <DividerLine />
          </Inline>

          <Rows spacing={8}>
            <TextSmallLight>Invite by email</TextSmallLight>
            {/* It'd be nice to have a chips input component.  But it seems so tied into the submitting model now that IDK a nice api. */}
            <Inline spacing={8} yalign="center">
              <div className={styles.emailInputContainer}>
                <FormInput
                  size="large"
                  chips={input.chipEmails.map((e) => (
                    <Badge
                      key={e}
                      variant="grey2"
                      label={e}
                      onRemove={() =>
                        setInput((input) => ({
                          ...input,
                          chipEmails: input.chipEmails.filter((v) => v !== e),
                        }))
                      }
                    />
                  ))}
                  type="email"
                  value={input.composing}
                  onChange={(v) =>
                    setInput((input) => ({
                      ...input,
                      composing: v,
                      composingError: '',
                    }))
                  }
                  placeholder="Enter emails, comma separated"
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Backspace' &&
                      e.currentTarget.value === '' &&
                      input.chipEmails.length > 0
                    ) {
                      setInput((input) => ({
                        ...input,
                        composing:
                          input.chipEmails[input.chipEmails.length - 1],
                        chipEmails: input.chipEmails.slice(
                          0,
                          input.chipEmails.length - 1,
                        ),
                      }));
                      e.preventDefault();
                    }
                    if (e.key === ' ' || e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      maybeAddComposing();
                    }
                  }}
                  onBlur={() => {
                    clearTimeout(blurTimeout.current);
                    // Shifting the layout would cause the submit button to move out
                    // from under the mouse, loosing the click.  Hence 100ms delay:
                    blurTimeout.current = setTimeout(maybeAddComposing, 100);
                  }}
                />
              </div>
              <div className={styles.fixedWidthButtonContainer}>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  label="Invite"
                  disabled={emails.length === 0}
                  loading={loading}
                  fullWidth={true}
                />
              </div>
            </Inline>
            {input.composingError && (
              <TextSmallLight color="red">
                {input.composingError}
              </TextSmallLight>
            )}
          </Rows>
          <div className={styles.textContainer}>
            <TextSmallLight color="grey">
              Additional seats may be added to your invoice when team members
              accept, <Link href={manageUrl}>at your current price</Link>.
            </TextSmallLight>
            <RedErrorText error={formError} />
          </div>
        </Rows>
      </div>
    </InvisibleForm>
  );
}

const InviteLinkOptionsMenu = observer(function _InviteLinkOptionsMenu({
  enabled,
  onReset,
  onToggle,
}: {
  enabled: boolean;
  onReset: () => Promise<void>;
  onToggle: (enabled: boolean) => Promise<void>;
}) {
  const [toggleLoading, _, handleToggle] = useSubmit(async () => {
    await onToggle(!enabled);
  });
  const [resetLoading, __, handleReset] = useSubmit(onReset);
  const loading = toggleLoading || resetLoading;

  const menu = [
    menuItem({
      label: 'Reset invite link',
      onClick: handleReset,
      disabled: loading,
    }),
    menuItem({
      label: enabled ? 'Turn off invite links' : 'Turn on invite links',
      onClick: handleToggle,
      disabled: loading,
    }),
  ];

  return (
    <MenuButton
      variant="tertiary"
      icon={IconOption}
      menu={menu}
      search="hidden"
      disabled={loading}
      size="large"
    />
  );
});

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function setAppend(existing: readonly string[], add: string) {
  return [...existing.filter((v) => v !== add), add];
}
